// BreakthroughsPanel.jsx
import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Sparkles,
  Trophy,
  DollarSign,
  Users,
  Handshake,
  Briefcase,
  MoreHorizontal,
  Eye,
  EyeOff,
  Share2,
} from "lucide-react";
import ShareCardDialog from "@/components/ShareCardDialog";
import "./BreakthroughsPanel.css";

const CATEGORIES = [
  { id: "funding", label: "Funding", icon: DollarSign },
  { id: "hire", label: "Hire", icon: Users },
  { id: "intro", label: "Intro", icon: Handshake },
  { id: "deal", label: "Deal", icon: Briefcase },
  { id: "partnership", label: "Partnership", icon: Sparkles },
  { id: "other", label: "Other", icon: MoreHorizontal },
];

const ICON_BY_CAT = Object.fromEntries(CATEGORIES.map((c) => [c.id, c.icon]));

const STATIC_BREAKTHROUGHS = [
  {
    id: "b-1",
    text: "Closed a $1M seed round with a lead I met at Founders Tonight.",
    category: "funding",
    name: "Alex Chen",
    title: "Founder",
    company: "Nexa AI",
    anonymous: false,
    created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: "b-2",
    text: "Hired our new Head of Engineering — connected through the Climate Capital Salon.",
    category: "hire",
    name: "Jordan Martinez",
    title: "CTO",
    company: "Greentide",
    anonymous: false,
    created_at: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: "b-3",
    text: "Signed a strategic partnership with a Fortune 500 logistics company.",
    category: "partnership",
    name: "Sam Patel",
    title: "Head of BD",
    company: "ChainSync",
    anonymous: false,
    created_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: "b-4",
    text: "Got introduced to three LPs through a single dinner table. All three meetings happened this week.",
    category: "intro",
    name: null,
    title: null,
    company: null,
    anonymous: true,
    member_code: "M-4821",
    created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: "b-5",
    text: "Closed a $500K advisory deal with a climate-tech unicorn.",
    category: "deal",
    name: "Taylor Reed",
    title: "Advisor",
    company: "Reed Consulting",
    anonymous: false,
    created_at: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: "b-6",
    text: "Found our lead designer through the Craft + Design table. Started this week.",
    category: "hire",
    name: null,
    title: null,
    company: null,
    anonymous: true,
    member_code: "M-7392",
    created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: "b-7",
    text: "Met a co-investor who joined our Series A round with $2M.",
    category: "funding",
    name: "Priya Kapoor",
    title: "Partner",
    company: "Elevate VC",
    anonymous: false,
    created_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
  },
];

const STATIC_MY_IDS = ["b-1", "b-5"];

const STATIC_MY_URLS = {
  "b-1": null,
  "b-5": null,
};

function timeAgo(iso) {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch {
    return "";
  }
}

export default function BreakthroughsPanel({ eventId, scope = "event" }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [total30d, setTotal30d] = useState(0);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    text: "",
    category: "intro",
    anonymous: false,
  });
  const [saving, setSaving] = useState(false);
  const [myIds, setMyIds] = useState(new Set());
  const [myUrls, setMyUrls] = useState({});
  const [shareFor, setShareFor] = useState(null);

  const load = async () => {
    await new Promise((resolve) => setTimeout(resolve, 500));
    try {
      const filtered =
        scope === "event" && eventId
          ? STATIC_BREAKTHROUGHS.slice(0, 4)
          : STATIC_BREAKTHROUGHS;

      const countMap = {};
      STATIC_BREAKTHROUGHS.forEach((b) => {
        countMap[b.category] = (countMap[b.category] || 0) + 1;
      });
      const categoryCounts = Object.entries(countMap).map(
        ([category, count]) => ({ category, count }),
      );

      setItems(filtered);
      setCats(categoryCounts);
      setTotal30d(STATIC_BREAKTHROUGHS.length);
    } catch {
      /* soft fail */
    }
  };

  const loadMine = async () => {
    if (!user || typeof user !== "object") return;
    await new Promise((resolve) => setTimeout(resolve, 300));
    try {
      setMyIds(new Set(STATIC_MY_IDS));
      setMyUrls(STATIC_MY_URLS);
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    load();
  }, [eventId, scope]); // eslint-disable-line
  useEffect(() => {
    loadMine();
  }, [user]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (!form.text.trim()) return;
    setSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 700));
    try {
      const newId = `b-${Date.now()}`;
      const newBreakthrough = {
        id: newId,
        text: form.text.trim(),
        category: form.category,
        name: form.anonymous ? null : user?.name || "You",
        title: form.anonymous ? null : user?.title || "",
        company: form.anonymous ? null : user?.company || "",
        anonymous: form.anonymous,
        member_code: form.anonymous
          ? `M-${Math.floor(1000 + Math.random() * 9000)}`
          : null,
        created_at: new Date().toISOString(),
      };
      setItems((prev) => [newBreakthrough, ...prev]);
      setTotal30d((prev) => prev + 1);
      setMyIds((prev) => new Set([...prev, newId]));
      setMyUrls((prev) => ({ ...prev, [newId]: null }));
      toast.success("Breakthrough logged. Congratulations.");
      setOpen(false);
      setForm({ text: "", category: "intro", anonymous: false });
      setShareFor({
        id: newId,
        text: newBreakthrough.text,
        existingUrl: null,
      });
    } catch {
      toast.error("Failed to log breakthrough.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="section breakthroughsSection"
      data-testid="breakthroughs-panel"
    >
      <div className="container-x">
        {/* Header */}
        <div className="breakthroughsHeader">
          <div>
            <p className="overline breakthroughsOverline">
              <Trophy size={12} /> Outcomes
            </p>
            <h2 className="breakthroughsHeading">
              Breakthroughs{" "}
              <span className="breakthroughsHeadingAccent">
                from {scope === "event" ? "this room" : "the rooms"}.
              </span>
            </h2>
            {total30d > 0 && (
              <p className="breakthroughsSubtext">
                {total30d} members reported breakthroughs in the last 30 days
                {cats.length
                  ? ` — ${cats
                      .slice(0, 3)
                      .map((c) => `${c.count} ${c.category}`)
                      .join(", ")}`
                  : ""}
                .
              </p>
            )}
          </div>
          {user && (
            <button
              data-testid="breakthrough-open"
              onClick={() => setOpen(true)}
              className="btn-gold breakthroughsMarkBtn"
            >
              <Sparkles size={14} /> Mark a breakthrough
            </button>
          )}
        </div>

        {/* Category trend strip */}
        {cats.length > 0 && (
          <div
            className="breakthroughsCatStrip"
            data-testid="breakthroughs-cat-strip"
          >
            {cats.map((c) => {
              const Icon = ICON_BY_CAT[c.category] || MoreHorizontal;
              return (
                <span key={c.category} className="breakthroughsCatPill">
                  <Icon size={12} className="breakthroughsCatPillIcon" />
                  <span className="breakthroughsCatPillLabel">
                    {c.category}
                  </span>
                  <span className="breakthroughsCatPillCount">{c.count}</span>
                </span>
              );
            })}
          </div>
        )}

        {/* List */}
        {items.length === 0 ? (
          <p className="breakthroughsEmpty">
            No breakthroughs logged yet
            {scope === "event" ? " for this event" : ""}. Be the first to
            celebrate one.
          </p>
        ) : (
          <div className="breakthroughsGrid" data-testid="breakthroughs-list">
            {items.map((b) => {
              const Icon = ICON_BY_CAT[b.category] || MoreHorizontal;
              const displayName = b.anonymous
                ? `Member ${b.member_code}`
                : b.name || "Member";
              const sub = b.anonymous
                ? "Identity hidden by member"
                : [b.title, b.company].filter(Boolean).join(" · ");
              return (
                <article
                  key={b.id}
                  data-testid={`breakthrough-${b.id}`}
                  className="glass breakthroughCard"
                >
                  <div className="breakthroughCardTop">
                    <span className="breakthroughCardIconChip">
                      <Icon size={14} className="breakthroughCardIcon" />
                    </span>
                    <span className="overline breakthroughCardCat">
                      {b.category}
                    </span>
                    <span className="overline breakthroughCardTime">
                      {timeAgo(b.created_at)}
                    </span>
                  </div>
                  <p className="breakthroughCardText">{b.text}</p>
                  <div className="breakthroughCardFooter">
                    <div className="breakthroughCardAuthor">
                      <p className="breakthroughCardAuthorName">
                        {b.anonymous && (
                          <EyeOff
                            size={12}
                            className="breakthroughCardAnonIcon"
                          />
                        )}
                        {displayName}
                      </p>
                      {sub && (
                        <p className="overline breakthroughCardAuthorSub">
                          {sub}
                        </p>
                      )}
                    </div>
                    {myIds.has(b.id) && (
                      <button
                        onClick={() =>
                          setShareFor({
                            id: b.id,
                            text: b.text,
                            existingUrl: myUrls[b.id] || null,
                          })
                        }
                        data-testid={`breakthrough-share-${b.id}`}
                        title="Share this breakthrough"
                        className="breakthroughShareBtn"
                      >
                        <Share2 size={12} /> Share
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {/* Mark dialog */}
      {open && (
        <div
          className="breakthroughDialogOverlay"
          onClick={() => setOpen(false)}
        >
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="glass breakthroughDialogForm"
            data-testid="breakthrough-form"
          >
            <p className="overline breakthroughFormOverline">
              Mark a breakthrough
            </p>
            <h3 className="breakthroughFormHeading">What just happened?</h3>
            <p className="breakthroughFormDesc">
              One sentence. We celebrate it on the public feed.
            </p>
            <textarea
              required
              maxLength={500}
              rows={3}
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              placeholder="e.g. Closed a $1M seed round with a lead I met at Founders Tonight."
              data-testid="breakthrough-text"
              className="breakthroughFormTextarea"
            />
            <p className="breakthroughFormCharCount">{form.text.length}/500</p>

            <div
              className="breakthroughFormCatGrid"
              data-testid="breakthrough-cats"
            >
              {CATEGORIES.map((c) => {
                const Icon = c.icon;
                const active = form.category === c.id;
                return (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => setForm({ ...form, category: c.id })}
                    data-testid={`breakthrough-cat-${c.id}`}
                    className={`breakthroughFormCatBtn ${
                      active
                        ? "breakthroughFormCatBtnActive"
                        : "breakthroughFormCatBtnIdle"
                    }`}
                  >
                    <Icon size={14} /> {c.label}
                  </button>
                );
              })}
            </div>

            <label className="breakthroughFormAnonLabel">
              <input
                type="checkbox"
                checked={form.anonymous}
                onChange={(e) =>
                  setForm({ ...form, anonymous: e.target.checked })
                }
                data-testid="breakthrough-anonymous"
                className="breakthroughFormAnonCheckbox"
              />
              <span className="breakthroughFormAnonText">
                {form.anonymous ? (
                  <EyeOff size={14} className="breakthroughFormAnonIconGold" />
                ) : (
                  <Eye size={14} className="breakthroughFormAnonIconDim" />
                )}
                Post anonymously (your name will be hidden)
              </span>
            </label>

            <div className="breakthroughFormActions">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="btn-ghost breakthroughFormCancelBtn"
                data-testid="breakthrough-cancel"
              >
                Cancel
              </button>
              <button
                disabled={saving || !form.text.trim()}
                type="submit"
                className="btn-gold breakthroughFormSubmitBtn"
                data-testid="breakthrough-submit"
              >
                <Sparkles size={14} />{" "}
                {saving ? "Logging…" : "Log breakthrough"}
              </button>
            </div>
          </form>
        </div>
      )}

      {shareFor && (
        <ShareCardDialog
          breakthroughId={shareFor.id}
          breakthroughText={shareFor.text}
          existingUrl={shareFor.existingUrl}
          onClose={() => {
            setShareFor(null);
            loadMine();
          }}
        />
      )}
    </section>
  );
}
